import React, { useEffect, useState } from 'react';
import {
  Plus,
  Edit,
  Trash2,
  Search,
  Filter,
  Download,
  Save,
  X,
  FileText,
  DollarSign,
  Calendar,
  Store,
  CheckCircle,
  Clock,
  AlertCircle
} from 'lucide-react';
import { storeInvoicesAPI, storesAPI } from '../services/api';
import {
  ModernCard,
  ModernButton,
  ModernInput,
  ModernSelect,
  ModernTable,
  ModernBadge,
  ModernModal,
  ModernSearchBar,
  ModernLoadingSpinner
} from '../components/ui/ModernComponents';
import toast from 'react-hot-toast';
import EntitySelectWithAdd from '../components/ui/EntitySelectWithAdd';
import { generateSalesInvoicePDF } from '../utils/invoicePDF';
import { formatDateOnlyFr } from '../utils/dateFormat';
import { useAuth } from '../contexts/AuthContext';

const defaultForm = {
  store_id: '',
  sales_order_id: '',
  amount: '',
  date: '',
  status: 'pending',
};

const PointsDeVenteFactures = () => {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [stores, setStores] = useState([]);
  const [salesOrders, setSalesOrders] = useState([]); // New state for sales orders
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [editingId, setEditingId] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Helper to get store name
  const getStoreName = (storeId) => {
    const store = stores.find((s) => s.id === storeId);
    return store ? store.name : `Magasin ${storeId}`;
  };

  // Filtered invoices - moved here to fix initialization order
  const filteredInvoices = (invoices || []).filter(invoice => {
    const storeName = getStoreName(invoice.store_id);
    return String(storeName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
           String(invoice.amount || '').includes(searchTerm) ||
           String(invoice.date || '').includes(searchTerm) ||
           String(invoice.status || '').toLowerCase().includes(searchTerm.toLowerCase());
  });

  const paginatedInvoices = filteredInvoices.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const totalPages = Math.ceil(filteredInvoices.length / itemsPerPage);

  // Fetch invoices
  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const res = await storeInvoicesAPI.getStoreInvoices();
      console.log('Fetched invoices:', res.data);
      console.log('Invoice amounts:', res.data.map(inv => ({ id: inv.id, amount: inv.amount, type: typeof inv.amount })));
      setInvoices(res.data);
    } catch (err) {
      toast.error('Erreur lors du chargement des factures');
    }
    setLoading(false);
  };

  // Fetch stores for dropdown
  const fetchStores = async () => {
    try {
      const res = await storesAPI.getStores();
      const storesData = res.data;
      console.log('Fetched stores:', storesData);
      setStores(storesData);
    } catch (err) {
      console.error('Error fetching stores:', err);
      setStores([]);
    }
  };

  // Fetch sales orders for a specific store
  const fetchSalesOrdersForStore = async (storeId) => {
    if (!storeId) {
      setSalesOrders([]);
      return;
    }
    
    console.log('Fetching sales orders for store:', storeId);
    console.log('User store_id:', user?.store_id); // Debug user store
    console.log('User role:', user?.role); // Debug user role
    
    try {
      // Import the sales orders API
      const { salesOrdersAPI } = await import('../services/api');
      const res = await salesOrdersAPI.getSalesOrders({ store_id: storeId });
      const orders = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      
      console.log('All orders received:', orders);
      console.log('Orders for store', storeId, ':', orders.filter(o => o.store_id == storeId));
      
      const { normalizeLegacyStatus, LIVRE_STATUS_SET } = await import('../constants/salesOrderStatus');
      // Commandes livrées (statuts finaux) sans facture magasin
      const availableOrders = orders.filter(
        (order) =>
          LIVRE_STATUS_SET.has(normalizeLegacyStatus(order.status)) &&
          !invoices.some((inv) => inv.sales_order_id === order.id)
      );
      
      console.log('Available confirmed orders:', availableOrders);
      
      // Calculate total for each order if not already provided
      const ordersWithTotal = availableOrders.map(order => {
        if (order.order_total !== undefined) {
          return order;
        }
        // If no total provided, calculate from items if available
        if (order.items && Array.isArray(order.items)) {
          const total = order.items.reduce((sum, item) => sum + (Number(item.price || 0) * Number(item.quantity || 1)), 0);
          return { ...order, order_total: total };
        }
        return { ...order, order_total: 0 };
      });
      
      console.log('Orders with calculated totals:', ordersWithTotal);
      setSalesOrders(ordersWithTotal);
    } catch (err) {
      console.error('Error fetching sales orders:', err);
      setSalesOrders([]);
    }
  };

  useEffect(() => {
    fetchInvoices();
    fetchStores(); // Fetch stores when component mounts
  }, []);

  // Handle form input
  const handleChange = (e) => {
    let name, value;
    
    // Handle both input fields (e.target) and select fields (direct value)
    if (e && e.target) {
      // Input field with e.target
      name = e.target.name;
      value = e.target.value;
    } else {
      // Select field with direct value (e is the value)
      // For ModernSelect, we need to get the name from the current form context
      // This is a limitation - we'll need to handle selects differently
      return;
    }
    
    setForm((f) => ({ ...f, [name]: value }));
    
    // If store is changed, fetch sales orders for that store
    if (name === 'store_id') {
      fetchSalesOrdersForStore(value);
      // Reset sales order selection when store changes
      setForm(f => ({ ...f, sales_order_id: '' }));
    }
  };

  // Helper function to find option by value
  const findOptionByValue = (options, value) => {
    return options.find(option => option.value === value) || null;
  };

  // Handle select field changes (for ModernSelect components)
  const handleSelectChange = (fieldName, selectedOption) => {
    const value = selectedOption ? selectedOption.value : '';
    console.log('Select change:', fieldName, 'value:', value, 'type:', typeof value);
    
    setForm((f) => ({ ...f, [fieldName]: value }));
    
    // If store is changed, fetch sales orders for that store
    if (fieldName === 'store_id') {
      console.log('Store changed to:', value, 'type:', typeof value);
      fetchSalesOrdersForStore(value);
      // Reset sales order selection when store changes
      setForm(f => ({ ...f, sales_order_id: '' }));
    }
    
    // If sales order is selected, auto-fill the amount
    if (fieldName === 'sales_order_id' && value) {
      const selectedOrder = salesOrders.find(order => order.id.toString() === value);
      if (selectedOrder && selectedOrder.order_total) {
        setForm(f => ({ ...f, amount: selectedOrder.order_total }));
      }
    }
  };

  // Handle add/edit submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);
    
    try {
      // Prepare form data - convert empty string to null for sales_order_id
      const formData = {
        ...form,
        sales_order_id: form.sales_order_id === '' ? null : form.sales_order_id
      };
      
      console.log('Submitting form data:', formData);
      
      if (editingId) {
        await storeInvoicesAPI.updateStoreInvoice(editingId, formData);
        toast.success('Facture modifiée');
      } else {
        await storeInvoicesAPI.createStoreInvoice(formData);
        toast.success('Facture ajoutée');
      }
      setShowForm(false);
      setForm(defaultForm);
      setEditingId(null);
      fetchInvoices();
    } catch (err) {
      console.error('Error submitting form:', err);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSubmitLoading(false);
    }
  };

  // Handle edit
  const handleEdit = (invoice) => {
    setForm({
      store_id: invoice.store_id?.toString() || '',
      sales_order_id: invoice.sales_order_id?.toString() || '',
      amount: invoice.amount?.toString() || '',
      date: invoice.date ? invoice.date.slice(0, 10) : '',
      status: invoice.status || 'pending',
    });
    setEditingId(invoice.id);
    setShowForm(true);
    
    // Fetch sales orders for the store when editing
    if (invoice.store_id) {
      fetchSalesOrdersForStore(invoice.store_id);
    }
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette facture ? Cette action est irréversible.')) return;
    try {
      await storeInvoicesAPI.deleteStoreInvoice(id);
      toast.success('Facture supprimée');
      fetchInvoices();
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Handle add new
  const handleAdd = () => {
    setForm({
      store_id: '',
      sales_order_id: '',
      amount: '',
      date: new Date().toISOString().slice(0, 10),
      status: 'pending',
    });
    setEditingId(null);
    setShowForm(true);
    setSalesOrders([]); // Clear sales orders when adding new
  };

  const handleExport = async () => {
    try {
      const response = await storeInvoicesAPI.exportStoreInvoices();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'store_invoices.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      toast.error("Erreur lors de l'export des factures");
    }
  };

  // Helper to generate PDF invoice for a store invoice
  const generateInvoicePDF = async (invoice) => {
    try {
      // Get store name
      const store = stores.find(s => s.id === invoice.store_id);
      const storeName = store ? store.name : `Point de vente ${invoice.store_id}`;
      
      // Create a simple item for the invoice
      const items = [{
        product_name: 'Montant de la facture',
        quantity: 1,
        price: invoice.amount,
        item_type: 'service'
      }];
      
      generateSalesInvoicePDF(invoice, storeName, items, []);
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Erreur lors de la génération du PDF');
    }
  };

  const handleViewDetails = (invoice) => {
    setSelectedInvoice(invoice);
    setDetailsModalOpen(true);
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { variant: 'warning', label: 'En attente' },
      paid: { variant: 'success', label: 'Payée' },
      cancelled: { variant: 'danger', label: 'Annulée' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    return <ModernBadge variant={config.variant}>{config.label}</ModernBadge>;
  };

  const formatCurrency = (amount) => {
    // Ensure amount is a valid number
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) {
      console.error('Invalid amount value:', amount, 'type:', typeof amount);
      return '0,00 MAD';
    }
    
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'MAD',
    }).format(numAmount);
  };

  const formatDate = (dateString) => formatDateOnlyFr(dateString);

  const storeOptions = stores.map(store => ({
    value: store.id.toString(),
    label: store.name
  }));

  console.log('Store options:', storeOptions);

  const salesOrderOptions = salesOrders.map(order => ({
    value: order.id.toString(),
    label: `Commande #${order.id} - ${order.client_name || 'Client'} - ${formatCurrency(order.order_total || 0)}`
  }));

  const statusOptions = [
    { value: 'pending', label: 'En attente' },
    { value: 'paid', label: 'Payée' },
    { value: 'cancelled', label: 'Annulée' }
  ];

  const tableColumns = [
    {
      key: 'store_id',
      label: 'Magasin',
      render: (value) => (
        <div className="flex items-center">
          <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
            <Store className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <div className="font-semibold text-neutral-900">{getStoreName(value)}</div>
          </div>
        </div>
      )
    },
    {
      key: 'sales_order_id',
      label: 'Commande liée',
      render: (value, invoice) => (
        value ? (
          <div className="flex items-center">
            <div className="w-8 h-8 bg-info-100 rounded-lg flex items-center justify-center mr-2">
              <FileText className="w-4 h-4 text-info-600" />
            </div>
            <div>
              <div className="text-sm font-medium text-info-700">#{value}</div>
              {invoice.client_name && (
                <div className="text-xs text-neutral-500">{invoice.client_name}</div>
              )}
            </div>
          </div>
        ) : (
          <span className="text-sm text-neutral-400">Aucune</span>
        )
      )
    },
    {
      key: 'amount',
      label: 'Montant',
      render: (value) => (
        <span className="font-semibold text-success-600">
          {formatCurrency(value)}
        </span>
      )
    },
    {
      key: 'date',
      label: 'Date',
      render: (value) => (
        <div className="flex items-center">
          <Calendar className="w-4 h-4 text-neutral-400 mr-2" />
          <span className="text-sm">{formatDate(value)}</span>
        </div>
      )
    },
    {
      key: 'status',
      label: 'Statut',
      render: (value) => getStatusBadge(value)
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (value, invoice) => (
        <div className="flex items-center space-x-2">
          <ModernButton
            variant="ghost"
            size="sm"
            icon={<Edit className="w-4 h-4" />}
            onClick={() => handleViewDetails(invoice)}
          >
            Détails
          </ModernButton>
          <ModernButton
            variant="primary"
            size="sm"
            icon={<Edit className="w-4 h-4" />}
            onClick={() => handleEdit(invoice)}
          >
            Modifier
          </ModernButton>
          <ModernButton
            variant="info"
            size="sm"
            icon={<FileText className="w-4 h-4" />}
            onClick={() => generateInvoicePDF(invoice)}
          >
            Facture
          </ModernButton>
          <ModernButton
            variant="danger"
            size="sm"
            icon={<Trash2 className="w-4 h-4" />}
            onClick={() => handleDelete(invoice.id)}
          >
            Supprimer
          </ModernButton>
        </div>
      )
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <ModernLoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Factures (Points de Vente)</h1>
          <p className="text-neutral-600">Gérez les factures de vos points de vente</p>
        </div>
        <div className="flex items-center space-x-3">
          <ModernSearchBar
            placeholder="Rechercher des factures..."
            onSearch={setSearchTerm}
            className="w-64"
            debounce={300}
          />
          <ModernButton
            variant="primary"
            icon={<Plus className="w-4 h-4" />}
            onClick={handleAdd}
          >
            Ajouter une facture
          </ModernButton>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Total Factures</p>
              <p className="text-2xl font-bold text-neutral-900">{(invoices || []).length}</p>
            </div>
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
              <FileText className="w-6 h-6 text-primary-600" />
            </div>
          </div>
        </ModernCard>

        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Payées</p>
              <p className="text-2xl font-bold text-success-600">
                {(invoices || []).filter(inv => inv.status === 'paid').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-success-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-success-600" />
            </div>
          </div>
        </ModernCard>

        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">En Attente</p>
              <p className="text-2xl font-bold text-warning-600">
                {(invoices || []).filter(inv => inv.status === 'pending').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-warning-100 rounded-lg flex items-center justify-center">
              <Clock className="w-6 h-6 text-warning-600" />
            </div>
          </div>
        </ModernCard>

        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Total Montant</p>
              <p className="text-2xl font-bold text-primary-600">
                {(() => {
                  const total = (invoices || []).reduce((sum, inv) => {
                    const amount = parseFloat(inv.amount) || 0;
                    console.log(`Invoice ${inv.id}: amount=${inv.amount}, parsed=${amount}`);
                    return sum + amount;
                  }, 0);
                  
                  console.log('Total calculated:', total, 'isNaN:', isNaN(total));
                  
                  if (isNaN(total)) {
                    console.error('Total calculation resulted in NaN. Invoices:', invoices);
                    return '0,00 MAD';
                  }
                  
                  return formatCurrency(total);
                })()}
              </p>
            </div>
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-primary-600" />
            </div>
          </div>
        </ModernCard>
      </div>

      {/* Invoices Table */}
      <ModernCard className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-neutral-900">Liste des factures</h3>
            <p className="text-sm text-neutral-600">{filteredInvoices.length} factures trouvées</p>
          </div>
          <div className="flex items-center space-x-2">
            <ModernButton variant="ghost" size="sm" icon={<Filter className="w-4 h-4" />}>
              Filtrer
            </ModernButton>
            <ModernButton variant="ghost" size="sm" icon={<Download className="w-4 h-4" />} onClick={handleExport} title="Exporter les factures au format CSV">
              Exporter
            </ModernButton>
          </div>
        </div>

        <ModernTable
          columns={tableColumns}
          data={paginatedInvoices}
          className="w-full"
        />
        {paginatedInvoices.length === 0 && (
          <div className="text-center text-neutral-400 py-8">Aucune facture trouvée.</div>
        )}
        {totalPages > 1 && (
          <div className="flex justify-center mt-4 space-x-2">
            <ModernButton variant="secondary" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>Précédent</ModernButton>
            <span className="px-2 py-1">Page {currentPage} / {totalPages}</span>
            <ModernButton variant="secondary" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>Suivant</ModernButton>
          </div>
        )}
      </ModernCard>

      {/* Add/Edit Modal */}
      <ModernModal
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        title={editingId ? 'Modifier la facture' : 'Ajouter une facture'}
        size="full"
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <ModernSelect
              label="Magasin"
              name="store_id"
              value={findOptionByValue(storeOptions, form.store_id)}
              onChange={(selectedOption) => handleSelectChange('store_id', selectedOption)}
              options={storeOptions}
              placeholder="Sélectionner un magasin..."
              required
            />

            <ModernSelect
              label="Commande (optionnel)"
              name="sales_order_id"
              value={findOptionByValue([
                { value: '', label: 'Aucune commande' },
                ...salesOrderOptions
              ], form.sales_order_id)}
              onChange={(selectedOption) => handleSelectChange('sales_order_id', selectedOption)}
              options={[
                { value: '', label: 'Aucune commande' },
                ...salesOrderOptions
              ]}
              placeholder="Sélectionner une commande..."
              disabled={!form.store_id || salesOrders.length === 0}
            />

            <ModernInput
              label="Montant"
              name="amount"
              type="number"
              min="0"
              step="0.01"
              value={form.amount}
              onChange={handleChange}
              placeholder="0.00"
              required
            />

            <ModernInput
              label="Date"
              name="date"
              type="date"
              value={form.date}
              onChange={handleChange}
              required
            />

            <ModernSelect
              label="Statut"
              name="status"
              value={findOptionByValue(statusOptions, form.status)}
              onChange={(selectedOption) => handleSelectChange('status', selectedOption)}
              options={statusOptions}
              required
            />
          </div>

          <div className="flex items-center justify-end space-x-3 pt-4">
            <ModernButton
              type="button"
              variant="secondary"
              onClick={() => setShowForm(false)}
            >
              Annuler
            </ModernButton>
            <ModernButton
              type="submit"
              variant="primary"
              icon={<Save className="w-4 h-4" />}
              disabled={submitLoading}
            >
              {submitLoading ? 'Enregistrement...' : (editingId ? 'Modifier' : 'Ajouter')}
            </ModernButton>
          </div>
        </form>
      </ModernModal>

      {/* Details Modal */}
      <ModernModal isOpen={detailsModalOpen} onClose={() => setDetailsModalOpen(false)} title="Détails de la facture" size="md">
        {selectedInvoice && (
          <div className="space-y-2">
            <div><b>ID:</b> {selectedInvoice.id}</div>
            <div><b>Magasin:</b> {getStoreName(selectedInvoice.store_id)}</div>
            {selectedInvoice.sales_order_id && (
              <>
                <div><b>Commande liée:</b> #{selectedInvoice.sales_order_id}</div>
                {selectedInvoice.client_name && (
                  <div><b>Client:</b> {selectedInvoice.client_name}</div>
                )}
                {selectedInvoice.order_total && (
                  <div><b>Montant de la commande:</b> {formatCurrency(selectedInvoice.order_total)}</div>
                )}
              </>
            )}
            <div><b>Montant:</b> {formatCurrency(selectedInvoice.amount)}</div>
            <div><b>Date:</b> {formatDate(selectedInvoice.date)}</div>
            <div><b>Statut:</b> {getStatusBadge(selectedInvoice.status)}</div>
          </div>
        )}
      </ModernModal>
    </div>
  );
};

export default PointsDeVenteFactures; 